import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN } from 'luxPlus/constants/base'
import { LE_LIVE_PROMOTION_GEO_STATES } from 'constants/config/region'
import { dateIsBefore, dateIsAfterOrSame } from 'lib/datetime/dateUtils'
import { isBannerDismissed, recordSiteBannerDismissal } from 'storage/siteBanner'
import { useIsBannerHidden } from 'hooks/useIsBannerHidden'
import useSwitchRegion from 'hooks/useSwitchRegion'
import ModalContext from 'contexts/ModalContext'
import LuxPlusFlightVoucherModal from 'luxPlus/components/modals/LuxPlusFlightVoucherModal'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'

const BANNER_ID = 'luxPlusFlightVoucherOfferBanner'

function useLuxPlusFlightVoucherBanner() {
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const geoStateName = useAppSelector(state => state.geo.geoStateName)
  const currentRegionCodeLower = currentRegionCode.toLowerCase()
  const memberSubscriptionItem = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const isLuxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const disableMembership = useAppSelector(state => state.auth.account.luxPlus.member.disableMembership)
  const isHidden = useIsBannerHidden(BANNER_ID)

  const now = new Date()
  const isCampaignActive = dateIsAfterOrSame(now, LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.START_DATE) &&
    dateIsBefore(now, LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.END_DATE)

  const [isDismissed, setIsDismissed] = useState(false)

  useEffect(() => {
    setIsDismissed(isBannerDismissed(BANNER_ID))
  }, [])

  const onDismiss = useCallback(() => {
    setIsDismissed(true)
    recordSiteBannerDismissal(BANNER_ID, LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.END_DATE.toISOString())
  }, [])

  const intl = useIntl()
  const memberRegion = memberSubscriptionItem?.region?.toLowerCase()
  const [, handleSwitchRegion] = useSwitchRegion(memberRegion)
  const showModal = useContext(ModalContext)

  const canRedeemLuxPlusBenefitsAnyRegion =
    isLuxPlusEnabled &&
    memberSubscriptionItem?.status === 'ACTIVE' &&
    memberSubscriptionItem?.tier !== 'free_preview' &&
    !disableMembership

  const isMember = canRedeemLuxPlusBenefitsAnyRegion && !!memberRegion
  const isEligibleRegionMember = isMember && memberRegion === LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.ELIGIBLE_REGION
  const isNonEligibleRegionMember = isMember && !isEligibleRegionMember
  const isCurrentMemberInHomeRegion = canRedeemLuxPlusBenefits && isMember && (memberRegion === currentRegionCodeLower)
  const isShoppingInEligibleRegion = currentRegionCodeLower === LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.ELIGIBLE_REGION

  const openFlightVoucherModal = useCallback(() => {
    showModal(<LuxPlusFlightVoucherModal />)
  }, [showModal])

  let ctaLabel: string
  let ctaTo: string | undefined
  let ctaHref: string | undefined
  let ctaOnClick: (() => void) | undefined

  const isLeLivePromotionState = !!geoStateName && LE_LIVE_PROMOTION_GEO_STATES.includes(geoStateName)

  const showBanner = isCampaignActive && !isDismissed && !isHidden && !isNonEligibleRegionMember && !isLeLivePromotionState && !isAgentHubEnabled &&
    (isEligibleRegionMember || isShoppingInEligibleRegion)

  if (isCurrentMemberInHomeRegion && isEligibleRegionMember) {
    ctaLabel = intl.formatMessage({
      id: 'DpRmlKoEdcJKfAodMVbe',
      defaultMessage: 'View details',
      description: 'CTA button label for LuxPlus+ flight voucher banner when member is in their home region',
    })
    ctaOnClick = openFlightVoucherModal
  } else if (isEligibleRegionMember) {
    ctaLabel = intl.formatMessage({
      id: 'FoCS6E_KYWcmIidXjrqi',
      defaultMessage: 'Switch to Home Region',
      description: 'CTA button label for LuxPlus+ flight voucher banner when member is in a different region',
    })
    ctaHref = `/${memberRegion}`
    ctaOnClick = handleSwitchRegion
  } else {
    ctaLabel = intl.formatMessage({
      id: '75nfhxK0v8dw6Ns6apVY',
      defaultMessage: 'Become a member',
      description: 'CTA button label for LuxPlus+ flight voucher banner for non-members',
    })
    ctaTo = '/lux-plus?luxplusExclusiveOffer=true'
  }

  return useMemo(() => ({
    showBanner,
    onDismiss,
    ctaLabel,
    ctaTo,
    ctaHref,
    ctaOnClick,
  }), [ctaHref, ctaLabel, ctaOnClick, ctaTo, onDismiss, showBanner])
}

export default useLuxPlusFlightVoucherBanner
